/**
 * Socket Service — breaks the circular dependency between stores.
 *
 * connectionStore registers accessor functions after socket creation.
 * taskStore and historyStore read socket state through these accessors
 * instead of importing connectionStore directly.
 */

import type { Socket } from "socket.io-client";
import type { PayloadOf, RelayForwardTags, TypedEventName } from "@bash/shared";

/**
 * What a browser handler receives for event E: the wire contract's payload
 * (`PayloadOf<E>`, derived from the event registry's schema) plus the routing
 * tags the relay stamps on every frame it forwards sandbox→browser. The tags
 * are optional here because a server-originated emit carries none of them.
 */
export type ClientPayloadOf<E extends TypedEventName> = PayloadOf<E> & Partial<RelayForwardTags>;

/**
 * Typed `socket.on`: the handler's payload parameter IS the contract's type for
 * that event, so a handler can no longer re-author the shape inline from what
 * it saw on the wire. Only events the contract types are accepted — an untyped
 * event fails at the call site, which is the prompt to author its schema.
 *
 * Returns the matching `off`, so a register function can collect cleanups.
 * Runtime is exactly `socket.on` / `socket.off`; the value is in the types.
 */
export function onEvent<E extends TypedEventName>(
  socket: Socket,
  event: E,
  handler: (payload: ClientPayloadOf<E>) => void,
): () => void {
  // socket.io types a listener as (...args: any[]) => void and cannot resolve
  // its reserved-event conditional for a generic E; the payload type is
  // enforced on `handler` above, so neither cast narrows anything the caller
  // sees.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const listener = handler as (...args: any[]) => void;
  const name: string = event;
  socket.on(name, listener);
  return () => {
    socket.off(name, listener);
  };
}

type SocketAccessors = {
  getSocket: () => Socket | null;
  ensureConnected: () => void;
};

let _accessors: SocketAccessors = {
  getSocket: () => null,
  ensureConnected: () => {},
};

// Separate accessor for selectedTaskId — registered by taskStore to break the
// circular dependency (connectionStore → taskStore). Previously used require() hack.
let _getSelectedTaskId: () => string | null = () => null;

/** Called by taskStore at module init to register its selectedTaskId accessor. */
export function registerSelectedTaskIdAccessor(fn: () => string | null): void {
  _getSelectedTaskId = fn;
}

export function registerSocketAccessors(fns: SocketAccessors): void {
  _accessors = fns;
}

export function getSocket(): Socket | null {
  return _accessors.getSocket();
}

export function getSelectedTaskId(): string | null {
  return _getSelectedTaskId();
}

/** Reconnect the socket if it's disconnected (e.g. after exhausted retries). */
export function ensureConnected(): void {
  _accessors.ensureConnected();
}
